// Request telemetry per route pattern: how much each endpoint is called, how
// often it fails on our side (5xx), and how slow it is at the median and the
// tail. Backed by five-minute buckets summed across nodes, so the numbers are
// a minute or so behind and survive a deploy — unlike the Live tab's counters.
import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Loader2 } from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useTelemetry, type LogRange, type RouteStat } from "@/hooks/api";
import { cn } from "@/lib/cn";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { RangePicker, parseRange } from "./range-picker";

const nf = new Intl.NumberFormat("nb-NO");

// Recharts takes colours as strings, so these read the theme variables rather
// than the palette: a hardcoded hex keeps the dark chrome on a light theme.
// admin-positive rather than admin-primary: identical gold by default, but
// rigg's primary is a red, which would leave the OK and 5xx halves of the
// stack the same colour.
const BAR_COLOR = "var(--color-admin-positive)";
const ERROR_COLOR = "var(--color-admin-danger-400)";
const LATENCY_COLOR = "var(--color-admin-info)";
const GRID_COLOR = "var(--color-admin-card-border)";
const axisStyle = { fontSize: 11, fill: "var(--color-admin-text-subtle)" };

// A p95 past a second is where a screen starts to feel stuck.
const SLOW_MS = 1000;
const VERY_SLOW_MS = 2500;

type SortKey = "count" | "errorRate" | "p95Ms" | "route";

function bucketLabel(iso: string, range: LogRange): string {
  const d = new Date(iso);
  if (range === "1h" || range === "24h") {
    return d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
  }
  if (range === "7d") {
    const day = d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short" });
    const time = d.toLocaleTimeString("nb-NO", { hour: "2-digit", minute: "2-digit" });
    return `${day} ${time}`;
  }
  return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short" });
}

function latencyClass(ms: number): string {
  if (ms >= VERY_SLOW_MS) return "text-admin-danger-400";
  if (ms >= SLOW_MS) return "text-admin-warn-400";
  return "text-admin-text-primary";
}

function Stat({
  label,
  value,
  unit,
  secondary,
  valueClass = "text-admin-text-primary",
  className = "bg-admin-card border-admin-card-border",
}: {
  label: string;
  value: string;
  unit?: string;
  secondary: string;
  valueClass?: string;
  className?: string;
}) {
  return (
    <div className={`rounded-[10px] border px-4 py-3.5 flex flex-col gap-2 ${className}`}>
      <div className="text-[11px] font-semibold tracking-[0.06em] text-admin-text-subtle">
        {label}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span className={`text-[30px] font-bold leading-none tabular-nums ${valueClass}`}>
          {value}
        </span>
        {unit && <span className="text-[13px] text-admin-text-secondary">{unit}</span>}
      </div>
      <div className="text-xs text-admin-text-subtle">{secondary}</div>
    </div>
  );
}

function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-admin-card-border bg-admin-card px-3 py-2 shadow-lg">
      <p className="text-xs font-medium text-admin-text-primary mb-1">{label}</p>
      {payload.map((p: any) => (
        <p key={p.dataKey} className="text-xs text-admin-text-secondary flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-[2px]" style={{ background: p.color ?? p.fill }} />
          {p.name}: {nf.format(Number(p.value))}
          {p.dataKey === "p95" ? " ms" : ""}
        </p>
      ))}
    </div>
  );
}

function Legend({ items }: { items: { label: string; color: string }[] }) {
  return (
    <div className="flex items-center gap-3 text-[11px] text-admin-text-subtle">
      {items.map((i) => (
        <span key={i.label} className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-[2px]" style={{ background: i.color }} />
          {i.label}
        </span>
      ))}
    </div>
  );
}

function SortHeader({
  label,
  sortKey,
  active,
  onClick,
  className,
}: {
  label: string;
  sortKey: SortKey;
  active: SortKey;
  onClick: (k: SortKey) => void;
  className?: string;
}) {
  return (
    <TableHead className={className}>
      <button
        onClick={() => onClick(sortKey)}
        className={cn(
          "cursor-pointer hover:text-admin-text-primary",
          active === sortKey && "text-admin-text-primary font-semibold"
        )}
      >
        {label}
        {active === sortKey && " ↓"}
      </button>
    </TableHead>
  );
}

export function EndpointsTab() {
  const [params, setParams] = useSearchParams();
  const range = parseRange(params.get("range"));
  const [sort, setSort] = useState<SortKey>("count");
  const [query, setQuery] = useState("");
  const telemetry = useTelemetry(range);

  const setRange = (r: LogRange) => {
    const next = new URLSearchParams(params);
    next.set("range", r);
    setParams(next, { replace: true });
  };

  const rows = useMemo(() => {
    const all = telemetry.data?.routes ?? [];
    const q = query.trim().toLowerCase();
    const filtered = q ? all.filter((r) => `${r.method} ${r.route}`.toLowerCase().includes(q)) : all;
    const by: Record<SortKey, (a: RouteStat, b: RouteStat) => number> = {
      count: (a, b) => b.count - a.count,
      errorRate: (a, b) => b.errorRate - a.errorRate || b.status5xx - a.status5xx,
      p95Ms: (a, b) => b.p95Ms - a.p95Ms,
      route: (a, b) => a.route.localeCompare(b.route) || a.method.localeCompare(b.method),
    };
    return [...filtered].sort(by[sort]);
  }, [telemetry.data, query, sort]);

  const series = useMemo(
    () =>
      (telemetry.data?.series ?? []).map((p) => ({
        label: bucketLabel(p.bucket, range),
        ok: p.count - p.status5xx,
        feil: p.status5xx,
        p95: p.p95Ms,
      })),
    [telemetry.data, range]
  );

  const totals = telemetry.data?.totals;

  return (
    <div className="flex flex-col gap-4 mt-4">
      <div className="flex items-baseline gap-3.5 flex-wrap">
        <div className="text-2xl font-bold tracking-[-0.01em]">Endepunkter</div>
        <div className="text-[13px] text-admin-text-subtle">
          {telemetry.data
            ? `${telemetry.data.routes.length} ruter · ${nf.format(telemetry.data.totals.count)} kall`
            : telemetry.isLoading
              ? "laster…"
              : null}
        </div>
        <div className="flex-1" />
        <RangePicker value={range} onChange={setRange} />
      </div>

      {telemetry.isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-admin-text-secondary" />
        </div>
      )}

      {telemetry.isError && (
        <div className="rounded-[10px] border border-admin-danger-500/25 bg-admin-danger-500/[0.06] px-4 py-6 text-[13px] text-admin-text-secondary">
          <div className="font-semibold text-admin-danger-400 mb-1">Kunne ikke hente telemetri</div>
          {telemetry.error instanceof Error ? telemetry.error.message : "Ukjent feil"}
        </div>
      )}

      {totals && totals.count === 0 && (
        <div className="rounded-[10px] border border-admin-card-border bg-admin-card px-4 py-6 text-[13px] text-admin-text-subtle">
          Ingen telemetri i perioden ennå. Bøttene skrives hvert minutt, så det
          første datapunktet dukker opp like etter at backend har fått trafikk.
        </div>
      )}

      {totals && totals.count > 0 && (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Stat
              label="KALL"
              value={nf.format(totals.count)}
              secondary={`${nf.format(totals.status4xx)} svarte 4xx`}
            />
            <Stat
              label="5XX"
              value={nf.format(totals.status5xx)}
              secondary={`${totals.errorRate.toLocaleString("nb-NO")} % av alle kall`}
              valueClass={totals.status5xx > 0 ? "text-admin-danger-400" : "text-admin-text-primary"}
              className={
                totals.status5xx > 0
                  ? "bg-admin-danger-500/[0.06] border-admin-danger-500/25"
                  : "bg-admin-card border-admin-card-border"
              }
            />
            <Stat
              label="P50"
              value={nf.format(totals.p50Ms)}
              unit="ms"
              secondary={`snitt ${nf.format(totals.avgMs)} ms`}
              valueClass={latencyClass(totals.p50Ms)}
            />
            <Stat
              label="P95"
              value={nf.format(totals.p95Ms)}
              unit="ms"
              secondary={`maks ${nf.format(totals.maxMs)} ms`}
              valueClass={latencyClass(totals.p95Ms)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="rounded-[10px] border border-admin-card-border bg-admin-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold tracking-[0.06em] text-admin-text-subtle">
                  KALL PER INTERVALL
                </div>
                <Legend
                  items={[
                    { label: "OK / 4xx", color: BAR_COLOR },
                    { label: "5xx", color: ERROR_COLOR },
                  ]}
                />
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                  <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
                  <YAxis tick={axisStyle} allowDecimals={false} width={48} />
                  <Tooltip content={<ChartTooltip />} cursor={{ fill: "var(--color-admin-card-hover)" }} />
                  <Bar dataKey="ok" name="OK / 4xx" stackId="kall" fill={BAR_COLOR} />
                  <Bar dataKey="feil" name="5xx" stackId="kall" fill={ERROR_COLOR} radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="rounded-[10px] border border-admin-card-border bg-admin-card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="text-[11px] font-semibold tracking-[0.06em] text-admin-text-subtle">
                  P95 PER INTERVALL
                </div>
                <Legend items={[{ label: "p95, ms", color: LATENCY_COLOR }]} />
              </div>
              <ResponsiveContainer width="100%" height={200}>
                <LineChart data={series} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                  <XAxis dataKey="label" tick={axisStyle} interval="preserveStartEnd" />
                  <YAxis tick={axisStyle} allowDecimals={false} width={48} />
                  <Tooltip content={<ChartTooltip />} />
                  <Line
                    type="monotone"
                    dataKey="p95"
                    name="p95"
                    stroke={LATENCY_COLOR}
                    strokeWidth={2}
                    dot={false}
                    activeDot={{ r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="rounded-[10px] border border-admin-card-border bg-admin-card">
            <div className="flex items-center gap-3 px-4 py-3 border-b border-admin-card-border">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Filtrer på rute…"
                className="max-w-xs h-8 text-xs"
              />
              <span className="text-xs text-admin-text-subtle">
                {rows.length} av {telemetry.data!.routes.length} ruter
              </span>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <SortHeader label="Rute" sortKey="route" active={sort} onClick={setSort} />
                  <SortHeader label="Kall" sortKey="count" active={sort} onClick={setSort} className="text-right" />
                  <TableHead className="text-right">4xx</TableHead>
                  <TableHead className="text-right">5xx</TableHead>
                  <SortHeader label="5xx-andel" sortKey="errorRate" active={sort} onClick={setSort} className="text-right" />
                  <TableHead className="text-right">p50</TableHead>
                  <SortHeader label="p95" sortKey="p95Ms" active={sort} onClick={setSort} className="text-right" />
                  <TableHead className="text-right">Maks</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.method} ${r.route}`}>
                    <TableCell className="font-mono text-xs">
                      <span className="text-admin-text-subtle w-14 inline-block">{r.method}</span>
                      {r.route}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{nf.format(r.count)}</TableCell>
                    <TableCell className="text-right tabular-nums text-admin-text-secondary">
                      {r.status4xx ? nf.format(r.status4xx) : "–"}
                    </TableCell>
                    <TableCell className={cn("text-right tabular-nums", r.status5xx > 0 && "text-admin-danger-400 font-semibold")}>
                      {r.status5xx ? nf.format(r.status5xx) : "–"}
                    </TableCell>
                    <TableCell className={cn("text-right tabular-nums", r.status5xx > 0 && "text-admin-danger-400")}>
                      {r.status5xx ? `${r.errorRate.toLocaleString("nb-NO")} %` : "–"}
                    </TableCell>
                    <TableCell className={cn("text-right tabular-nums", latencyClass(r.p50Ms))}>
                      {nf.format(r.p50Ms)} ms
                    </TableCell>
                    <TableCell className={cn("text-right tabular-nums", latencyClass(r.p95Ms))}>
                      {nf.format(r.p95Ms)} ms
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-admin-text-secondary">
                      {nf.format(r.maxMs)} ms
                    </TableCell>
                  </TableRow>
                ))}
                {rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-admin-text-subtle py-6">
                      Ingen ruter matcher filteret.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
